import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import EnhancedPostContentManager from '../components/EnhancedPostContentManager';
import PostTitleManager from '../components/PostTitleManager';
import PublishButton from '../components/PublishButton';
import { FaArrowLeft, FaCheck, FaExclamationTriangle,  FaPlus, FaGripVertical, FaTrash, FaSearch } from 'react-icons/fa';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './ManageContentPage.css';
import './ModernUI.css';
import postService from "../services/postService";
import API_CONFIG from "../config/api";
import { getCategoryFeatures } from '../components/enhancedPostContentManager/config/categoryFeatures';

const SortablePostItem = ({ post, isActive, onSelect, statusInfo }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: post.id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`post-item ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
            onClick={() => onSelect(post)}
        >
            <div className="drag-handle" {...attributes} {...listeners}><FaGripVertical /></div>
            <div className="post-info">
                <div className="post-info-top">
                    <span className="text-muted text-xs">{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
                <h4 className="post-title">{post.title}</h4>
                <span className={`badge ${statusInfo.statusClass === 'published' ? 'badge-success' : 'badge-warning'}`}>
                    {statusInfo.statusText}
                </span>
            </div>
        </div>
    );
};

const ManageContentPage = () => {
    const pageId = sessionStorage.getItem('page_id');
    const { postId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    // State
    const [currentPost, setCurrentPost] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const successTimeoutRef = useRef(null);
    const errorTimeoutRef = useRef(null);
    const [pagesMeData, setPagesMeData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Refs for synchronization
    const isFetchingInitialRef = useRef(false);
    const prevPostIdRef = useRef(null);
    const features = getCategoryFeatures(pagesMeData?.category_id);

    // Auto-dismiss success and error messages
    useEffect(() => {
        if (success) {
            clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = setTimeout(() => {
                setSuccess('');
            }, 4000);
        }
        return () => clearTimeout(successTimeoutRef.current);
    }, [success]);

    useEffect(() => {
        if (error) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = setTimeout(() => {
                setError('');
            }, 5000);
        }
        return () => clearTimeout(errorTimeoutRef.current);
    }, [error]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // --- 1. INITIAL LOAD ---
    const loadInitialData = useCallback(async () => {
        if (!user?.id || isFetchingInitialRef.current || isInitialLoaded) return;

        try {
            isFetchingInitialRef.current = true;
            setIsLoading(true);

            const [posts, pageResponse] = await Promise.all([
                postService.getPosts({ creator_id: user.id }),
                axios.get(`${API_CONFIG.BASE_URL}/api/v1/pages/me`)
            ]);

            setUserPosts(posts);
            setPagesMeData(pageResponse.data);
            sessionStorage.setItem('pagesMeData', JSON.stringify(pageResponse.data));
            setIsInitialLoaded(true);
        } catch (err) {
            setError('Failed to load dashboard data');
        } finally {
            setIsLoading(false);
            isFetchingInitialRef.current = false;
        }
    }, [user?.id, isInitialLoaded]);

    // --- 2. BOOTSTRAP EFFECT (Linter Friendly) ---
    useEffect(() => {
        if (isAuthenticated && user && !isInitialLoaded && !isFetchingInitialRef.current) {
            loadInitialData();
        } else if (!isAuthenticated) {
            navigate('/login');
        }
    }, [isAuthenticated, user, isInitialLoaded, loadInitialData, navigate]);

    // --- 3. POST DETAIL SYNC ---
    useEffect(() => {
        if (!isInitialLoaded) return;

        // Auto-select first post if none in URL
        if (!postId && userPosts.length > 0) {
            navigate(`/manage-content/${userPosts[0].id}`, { replace: true });
            return;
        }

        // Fetch details only if URL ID changed
        if (postId && postId !== prevPostIdRef.current) {
            const fetchDetails = async () => {
                try {
                    prevPostIdRef.current = postId;
                    setIsDetailLoading(true);
                    const detailedPost = await postService.getPostById(postId);
                    setCurrentPost(detailedPost);
                    // Sync sidebar if title changed
                    setUserPosts(prev => prev.map(p => p.id === detailedPost.id ? detailedPost : p));
                } catch (err) {
                    setError('Failed to load post content');
                    prevPostIdRef.current = null;
                } finally {
                    setIsDetailLoading(false);
                }
            };
            fetchDetails();
        }
    }, [postId, isInitialLoaded, userPosts, navigate]);

    // --- Handlers ---
    const handlePostSelect = (post) => {
        if (postId === post.id.toString()) return;
        navigate(`/manage-content/${post.id}`);
    };

    const handlePostUpdate = (updatedPost) => {
        setCurrentPost(updatedPost);
        setUserPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = userPosts.findIndex((p) => p.id === active.id);
        const newIndex = userPosts.findIndex((p) => p.id === over.id);
        const reordered = arrayMove(userPosts, oldIndex, newIndex);
        setUserPosts(reordered);
        try {
            const updates = reordered.map((p, i) => ({ id: p.id, display_order: i }));
            await postService.reorderPosts(updates);
            setSuccess('Order updated');
            setTimeout(() => setSuccess(''), 2000);
        } catch (err) {
            setError('Failed to save order');
        }
    };

    const createNewPost = async () => {
        try {
            const newPost = await postService.createPost({
                page_id: pageId,
                title: features.createLabel.replace(/^New\s+/i, '') || 'New Post',
                post_type: features.postType,
                slug: `post-${Date.now()}`,
                is_published: false
            });
            setUserPosts(prev => [...prev, newPost]);
            navigate(`/manage-content/${newPost.id}`);
            setSuccess('Post created!');
        } catch (err) {
            setError('Create failed');
        }
    };

    const handleDeletePost = async () => {
        if (!currentPost) return;
        setIsDeleting(true);
        try {
            await postService.deletePost(currentPost.id);
            const remaining = userPosts.filter(p => p.id !== currentPost.id);
            setUserPosts(remaining);
            setShowDeleteModal(false);
            prevPostIdRef.current = null;
            navigate(remaining.length > 0 ? `/manage-content/${remaining[0].id}` : '/manage-content');
            setSuccess('Deleted');
        } catch (err) {
            setError('Delete failed');
        } finally {
            setIsDeleting(false);
        }
    };

    const getPostStatusInfo = (post) => ({
        statusText: post.is_published ? 'Published' : 'Draft',
        statusClass: post.is_published ? 'published' : 'draft',
    });

    if (isLoading && !isInitialLoaded) {
        return <div className="manage-content-page"><div className="p-5 text-center">Loading Content Manager...</div></div>;
    }

    return (
        <div className="manage-content-page">
            <div className="content-header">
                <div className="content-header-inner">
                    <div>
                        <Link to="/dashboard/content-creator" className="back-button-chip">
                            <FaArrowLeft/>
                            <span>Dashboard</span>
                        </Link>
                        <h1 className="content-title">Manage Content</h1>
                    </div>
                    {currentPost && (
                        <div className="header-actions">
                            <PublishButton
                                post={currentPost}
                                onPublishSuccess={(result, updatedPost) => {
                                    handlePostUpdate(updatedPost);
                                    setSuccess(updatedPost.is_published ? 'Post published successfully!' : 'Post unpublished successfully!');
                                }}
                                onPublishError={(error) => {
                                    setError(error.message || 'Failed to update post status');
                                }}
                                onStatusChange={(updatedPost) => {
                                    handlePostUpdate(updatedPost);
                                }}
                                categoryId={Number(pagesMeData?.category_id)}
                                className="publish-button-primary"
                            />
                            <button 
                                onClick={() => setShowDeleteModal(true)} 
                                className="delete-button-icon"
                                title="Delete post"
                            >
                                <FaTrash/>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-3">
                {success && (
                    <div className="toast-notification toast-success">
                        <div className="toast-icon">
                            <FaCheck />
                        </div>
                        <div className="toast-content">
                            <div className="toast-title">Success!</div>
                            <div className="toast-message">{success}</div>
                        </div>
                    </div>
                )}
                {error && (
                    <div className="toast-notification toast-error">
                        <div className="toast-icon">
                            <FaExclamationTriangle />
                        </div>
                        <div className="toast-content">
                            <div className="toast-title">Error</div>
                            <div className="toast-message">{error}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="content-layout">
                <div className="sidebar">
                    <div className="sidebar-header">
                        <h3 className="posts-label">Posts</h3>
                        <button onClick={createNewPost} className="new-post-button">
                            <FaPlus/>
                            <span>New</span>
                        </button>
                    </div>
                    <div className="search-bar-container">
                        <div className="search-input-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search posts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="search-clear-btn"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Clear search"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="posts-list">
                        {(() => {
                            const filteredPosts = searchQuery
                                ? userPosts.filter(post => post.title?.toLowerCase().includes(searchQuery.toLowerCase()))
                                : userPosts;
                            
                            if (filteredPosts.length === 0 && searchQuery) {
                                return (
                                    <div className="no-posts-found">
                                        <p>No posts found</p>
                                    </div>
                                );
                            }
                            
                            return (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={filteredPosts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                                        {filteredPosts.map(post => (
                                            <SortablePostItem
                                                key={post.id}
                                                post={post}
                                                isActive={currentPost?.id === post.id}
                                                onSelect={handlePostSelect}
                                                statusInfo={getPostStatusInfo(post)}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            );
                        })()}
                    </div>
                </div>

                <div className="main-content">
                    {isDetailLoading ? (
                        <div className="text-center p-5"><div className="spinner mb-3"></div><p>Loading details...</p></div>
                    ) : currentPost ? (
                        <div className="editor-view">
                            <div className="card mb-3">
                                <div className="card-body">
                                    <PostTitleManager currentPost={currentPost} onPostUpdate={handlePostUpdate} />
                                </div>
                            </div>
                            <EnhancedPostContentManager
                                key={currentPost.id}
                                postId={currentPost.id}
                                currentPost={currentPost}
                                pagesMeData={pagesMeData}
                                onPostUpdate={handlePostUpdate}
                            />
                        </div>
                    ) : (
                        <div className="empty-selection">Select a post to edit.</div>
                    )}
                </div>
            </div>

            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete this post?</h3>
                        <p>This cannot be undone.</p>
                        <div className="modal-actions">
                            <button onClick={() => setShowDeleteModal(false)} className="modal-cancel-btn">Cancel</button>
                            <button onClick={handleDeletePost} className="modal-delete-btn" disabled={isDeleting}>
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageContentPage;